import React, { useState } from 'react';
import { exportToExcel } from '../utils/exportToExcel';
import { amenitiesAPI } from '../services/api';
import useApi from '../hooks/useApi';
import './Amenities.css';

const Amenities = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: '',
    is_active: true
  });

  // Use API hook for amenities data
  const { data: amenitiesResponse = {}, loading, error, refetch } = useApi(amenitiesAPI.getAll);
  
  // Extract amenities array from the response structure
  const amenitiesArray = React.useMemo(() => {
    // Handle different possible response structures
    if (Array.isArray(amenitiesResponse)) {
      return amenitiesResponse;
    }
    if (amenitiesResponse && typeof amenitiesResponse === 'object') {
      // Check if it's the new structure with data.amenities
      if (amenitiesResponse.amenities && Array.isArray(amenitiesResponse.amenities)) {
        return amenitiesResponse.amenities;
      }
      // Check if it's the structure with data.data.amenities
      if (amenitiesResponse.data && amenitiesResponse.data.amenities && Array.isArray(amenitiesResponse.data.amenities)) {
        return amenitiesResponse.data.amenities;
      }
      // Check if it's directly the amenities array
      if (Array.isArray(amenitiesResponse.data)) {
        return amenitiesResponse.data;
      }
    }
    return [];
  }, [amenitiesResponse]);

  // Filter amenities based on search term
  const filteredAmenities = React.useMemo(() => {
    if (!searchTerm) return amenitiesArray;
    
    return amenitiesArray.filter(amenity => {
      const searchTermLower = searchTerm.toLowerCase();
      return (
        (amenity.name_en && amenity.name_en.toLowerCase().includes(searchTermLower)) ||
        (amenity.name_ar && amenity.name_ar.toLowerCase().includes(searchTermLower)) ||
        (amenity.description_en && amenity.description_en.toLowerCase().includes(searchTermLower)) ||
        (amenity.description_ar && amenity.description_ar.toLowerCase().includes(searchTermLower))
      );
    });
  }, [amenitiesArray, searchTerm]);

  // Map backend amenity data to frontend format
  const mapAmenityData = (amenity) => {
    // Handle both backend response formats
    const amenityData = amenity.amenity || amenity;
    
    return {
      id: amenityData.id,
      name_en: amenityData.name_en,
      name_ar: amenityData.name_ar,
      description_en: amenityData.description_en,
      description_ar: amenityData.description_ar,
      is_active: amenityData.is_active !== undefined ? amenityData.is_active : true
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data for submission
      const submitData = { ...formData };
      
      if (editingAmenity) {
        // Update existing amenity
        await amenitiesAPI.update(editingAmenity.id, submitData);
      } else {
        // Add new amenity
        await amenitiesAPI.create(submitData);
      }
      // Refresh the data
      refetch();
      resetForm();
    } catch (err) {
      console.error('Error saving amenity:', err);
      alert(err.response?.data?.message || 'Error saving amenity. Please try again.');
    }
  };

  const handleEdit = (amenity) => {
    // Map amenity data to form format
    const mappedAmenity = mapAmenityData(amenity);
    setEditingAmenity(amenity);
    setFormData({
      name_en: mappedAmenity.name_en || '',
      name_ar: mappedAmenity.name_ar || '',
      description_en: mappedAmenity.description_en || '',
      description_ar: mappedAmenity.description_ar || '',
      is_active: mappedAmenity.is_active !== undefined ? mappedAmenity.is_active : true
    });
    setShowForm(true);
    
    // Scroll to form to ensure user sees the edit form
    setTimeout(() => {
      const formElement = document.querySelector('.amenity-form-container');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this amenity? This action cannot be undone.')) {
      try {
        await amenitiesAPI.delete(id);
        // Refresh the data
        refetch();
      } catch (err) {
        console.error('Error deactivating amenity:', err);
        alert(err.response?.data?.message || 'Error deactivating amenity. Please try again.');
      }
    }
  };

  const handleStatusChange = async (amenityId, newStatus) => {
    try {
      const amenityToUpdate = amenitiesArray.find(amenity => amenity.id === amenityId);
      if (amenityToUpdate) {
        await amenitiesAPI.update(amenityId, { ...mapAmenityData(amenityToUpdate), is_active: newStatus });
        // Refresh the data
        refetch();
      }
    } catch (err) {
      console.error('Error updating amenity status:', err);
      alert(err.response?.data?.message || 'Error updating amenity status. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name_en: '',
      name_ar: '',
      description_en: '',
      description_ar: '',
      is_active: true
    });
    setEditingAmenity(null);
    setShowForm(false);
  };

  const getStatusClass = (status) => {
    return status ? 'status-active' : 'status-inactive';
  };

  // Export function
  const exportAmenitiesToExcel = () => {
    // Prepare data for export
    const exportData = amenitiesArray.map(amenity => {
      const mappedAmenity = mapAmenityData(amenity);
      return {
        ID: mappedAmenity.id || '',
        'Name (EN)': mappedAmenity.name_en || '',
        'Name (AR)': mappedAmenity.name_ar || '',
        'Description (EN)': mappedAmenity.description_en || '',
        'Description (AR)': mappedAmenity.description_ar || '',
        Status: mappedAmenity.is_active ? 'Active' : 'Inactive'
      };
    });
    
    exportToExcel(exportData, 'amenities', 'Amenities');
  };

  // Handle loading and error states
  if (loading) {
    return <div className="loading">Loading amenities...</div>;
  }

  if (error) {
    return <div className="error">Error loading amenities: {error.message}</div>;
  }

  return (
    <div className="amenities-page">
      <div className="page-header">
        <h1>Amenities Management</h1>
        <div>
          <button 
            className="btn btn-primary" 
            onClick={exportAmenitiesToExcel}
            style={{ marginRight: '10px' }}
          >
            Export to Excel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Add New Amenity'}
          </button>
        </div>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Search amenities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {showForm && (
        <div className="amenity-form-container">
          <h2>{editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}</h2>
          <form onSubmit={handleSubmit} className="amenity-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name_en">Name (English)*</label>
                <input
                  type="text"
                  id="name_en"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter amenity name in English"
                />
              </div>
              <div className="form-group">
                <label htmlFor="name_ar">Name (Arabic)</label>
                <input
                  type="text"
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleInputChange}
                  placeholder="Enter amenity name in Arabic"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="description_en">Description (English)</label>
                <textarea
                  id="description_en"
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  placeholder="Enter amenity description in English"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description_ar">Description (Arabic)</label>
                <textarea
                  id="description_ar"
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                  placeholder="Enter amenity description in Arabic"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label htmlFor="is_active" className="checkbox-label">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-text">Active</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingAmenity ? 'Update Amenity' : 'Add Amenity'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="amenities-list">
        <h2>Amenities List</h2>
        <table className="amenities-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name (EN)</th>
              <th>Name (AR)</th>
              <th>Description (EN)</th>
              <th>Description (AR)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAmenities.map(amenity => {
              const mappedAmenity = mapAmenityData(amenity);
              return (
                <tr key={mappedAmenity.id}>
                  <td>
                    <div className="amenity-id">{mappedAmenity.id || ''}</div>
                  </td>
                  <td>
                    <div className="amenity-name">{mappedAmenity.name_en || ''}</div>
                  </td>
                  <td>
                    <div className="amenity-name-ar">{mappedAmenity.name_ar || ''}</div>
                  </td>
                  <td>
                    <div className="amenity-description">
                      {mappedAmenity.description_en || ''}
                    </div>
                  </td>
                  <td>
                    <div className="amenity-description-ar">
                      {mappedAmenity.description_ar || ''}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(mappedAmenity.is_active)}`}>
                      {mappedAmenity.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-small btn-edit"
                        onClick={() => handleEdit(amenity)}
                      >
                        Edit
                      </button>
                      {mappedAmenity.is_active ? (
                        <button 
                          className="btn btn-small btn-delete"
                          onClick={() => handleStatusChange(amenity.id, false)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => handleStatusChange(amenity.id, true)}
                        >
                          Activate
                        </button>
                      )}
                      <button 
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(amenity.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredAmenities.length === 0 && (
          <div className="no-amenities">
            <p>
              {amenitiesArray.length === 0 
                ? 'No amenities found. Add your first amenity to get started!' 
                : 'No amenities match your search criteria.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Amenities;